import { GetExpenseResponse, GetPayUpType } from '@orbit/api-types'
import { useMemo } from 'react'
import { ActivityFilter, isFilterActive } from './ActivityFilterModal'
import { ActivityDateGroup, ActivityItem, groupActivityByDate } from './balancesFormat'

export type { ActivityDateGroup }

const matchesExpense = (
	expense: GetExpenseResponse,
	paidBySet: Set<number>,
	owesSet: Set<number>,
): boolean => {
	if (paidBySet.has(expense.paidByUserId)) return true
	if (owesSet.size > 0) {
		for (const p of expense.participants) {
			if (owesSet.has(p.userId)) return true
		}
	}
	return false
}

const matchesPayUp = (payUp: GetPayUpType, payUpSet: Set<number>): boolean => {
	return payUpSet.has(payUp.fromUserId) || payUpSet.has(payUp.toUserId)
}

/**
 * The member Balances activity feed: the shared merge/sort/date-group shape
 * (`groupActivityByDate`) with the page's activity filter layered on as the
 * item predicate.
 */
export const useGroupedActivity = (
	expenses: GetExpenseResponse[],
	payUps: GetPayUpType[],
	activityFilter: ActivityFilter,
): ActivityDateGroup[] => {
	return useMemo(() => {
		const active = isFilterActive(activityFilter)
		const paidBySet = new Set(activityFilter.paidByUserIds)
		const owesSet = new Set(activityFilter.owesUserIds)
		const payUpSet = new Set(activityFilter.payUpUserIds)

		const includeItem = (item: ActivityItem): boolean => {
			if (!active) return true
			if (item.kind === 'expense') return matchesExpense(item.data, paidBySet, owesSet)
			return matchesPayUp(item.data, payUpSet)
		}

		return groupActivityByDate(expenses, payUps, includeItem)
	}, [expenses, payUps, activityFilter])
}
